import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useAppDispatch } from "src/app/hooks";
import { usePatient } from "src/context/PatientProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import CloseIcon from "@mui/icons-material/Close";
import { patientApi } from "src/api/services/patientApi";
import { TAG } from "src/api/constants";
import { isString } from "src/utils/isString";
import { useSetDefaultPaymentMethodMutation } from "src/api/services/paymentMethodsApi";
import { useTranslation } from "react-i18next";
import { useCreateSetupIntentMutation } from "src/api/services/paymentsApi";

const styles = (isMobile: boolean) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: isMobile ? 327 : 600,
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  p: isMobile ? 2 : 4,
});

interface Props {
  onClose: () => void;
  open: boolean;
}

const AddNewPayment = ({ open, onClose }: Props) => {
  const isMobile = useIsMobile();
  const stripe = useStripe();
  const elements = useElements();
  const [markDefault, setMarkDefault] = useState(false);
  const [error, setError] = useState("");
  const [cardCompleted, setCardCompleted] = useState(false);
  const style = useMemo(() => styles(isMobile), [isMobile]);
  const dispatch = useAppDispatch();
  const firstName = usePatient((patient) => patient.profile.first_name);
  const lastName = usePatient((patient) => patient.profile.last_name);
  const [isLoading, setSubmitLoading] = useState(false);
  const [createSetUpIntent] = useCreateSetupIntentMutation();
  const [setupDefaultPayment] = useSetDefaultPaymentMethodMutation();
  const { t } = useTranslation();
  const component = "payment-methods.add-new-card";

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setMarkDefault(event.target.checked),
    [],
  );

  const handleCardChange = useCallback(
    (event: StripeCardElementChangeEvent) => {
      setCardCompleted(event.complete);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: SyntheticEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!stripe || !elements) return;

      const cardElement = elements.getElement(CardElement);

      if (!cardElement) return;

      try {
        setSubmitLoading(true);

        const { data } = await createSetUpIntent();

        const clientSecret = data?.clientSecret;

        if (!clientSecret) {
          setError("Oops, something went wrong");
          return;
        }

        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${firstName} ${lastName}`,
            },
          },
        });

        if (result.error) {
          setError(result.error.message || "Error confirming card");
          return;
        }

        if (
          markDefault === true &&
          isString(result.setupIntent.payment_method)
        ) {
          setupDefaultPayment({
            paymentMethodId: result.setupIntent.payment_method,
          });
        }

        dispatch(patientApi.util.invalidateTags([TAG.PAYMENT_METHODS]));
      } catch {
        setError("Oops, something went wrong. Please try again.");
      } finally {
        setSubmitLoading(false);
        onClose();
      }
    },
    [
      createSetUpIntent,
      dispatch,
      elements,
      firstName,
      lastName,
      markDefault,
      onClose,
      setupDefaultPayment,
      stripe,
    ],
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Stack sx={style} gap="16px" component="form" onSubmit={handleSubmit}>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              {t(`${component}.header`, "Add new card")}
            </Typography>
            <Typography variant="subtitle1">
              {t("transaction-history.pay-invoice-modal.subheader")}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ alignSelf: "baseline" }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box
          role="form"
          sx={{
            border: "1px solid var(--Secondary-200, #DCE4E8)",
            borderRadius: "10px",
            p: 3,
          }}
        >
          <CardElement
            onChange={handleCardChange}
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: "16px",
                  "::placeholder": {
                    color: "#1B1B1B",
                  },
                },
              },
            }}
          />
          {error && <Typography sx={{ color: "red" }}>{error}</Typography>}
        </Box>
        <FormControlLabel
          control={<Checkbox checked={markDefault} onChange={handleChange} />}
          label={t(`${component}.checkbox`, "Make default payment method")}
        />
        <Stack direction="row" gap="16px" alignSelf="end">
          <Button variant="alternate" onClick={onClose}>
            {t("account.overview.editModal.cancel")}
          </Button>
          <Button
            variant="contained"
            loading={isLoading}
            type="submit"
            disabled={!cardCompleted}
          >
            {t("account.overview.editModal.submit")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddNewPayment;
